import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Avatar, AvatarGroup, Tooltip,
    Grow, IconButton, InputAdornment, ToggleButton, ToggleButtonGroup, Card, CardContent
} from '@mui/material';
import { Add as AddIcon, ConfirmationNumber as ConfirmationNumberIcon, Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';

function UserTickets() {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    
    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/tickets`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const currentUserId = localStorage.getItem('user_id');
            const userTickets = res.data.filter(t => t.created_by_id === parseInt(currentUserId));
            setTickets(userTickets);
        } catch (e) {
            console.error("Error fetching tickets", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setCreating(true);

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_URL}/tickets/generate`, {
                subject: title,
                body: body
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setSuccess('Ticket created successfully!');
            setTitle('');
            setBody('');
            setOpenAddDialog(false);
            fetchTickets();
        } catch (e) {
            console.error("Failed to create ticket", e);
            setError(e.response?.data?.message || 'Failed to create ticket. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Done': return 'success';
            case 'In Progress': return 'info';
            case 'Open': return 'warning';
            case 'Reopen': return 'error';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critical': return 'error';
            case 'High': return 'warning';
            case 'Medium': return 'info';
            case 'Low': return 'success';
            default: return 'default';
        }
    };

    // Filter tickets
    const filteredTickets = tickets.filter(t => {
        const matchesSearch = t.title.toLowerCase().includes(search.toLowerCase()) ||
            (t.description && t.description.toLowerCase().includes(search.toLowerCase())) ||
            (String(t.id).includes(search));
        
        const matchesStatus = statusFilter === 'all' || 
            (statusFilter === 'active' && t.status !== 'Done') ||
            (statusFilter === 'closed' && t.status === 'Done');

        return matchesSearch && matchesStatus;
    });

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            
            {/* Modern Gradient Header */}
            <Grow in={true} timeout={500}>
                <Box sx={{ 
                    mb: 4, 
                    mt: 2, 
                    p: 4, 
                    borderRadius: 4, 
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                    color: 'white',
                    boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 3
                }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <ConfirmationNumberIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                            My Support Tickets
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Create and manage your specific support issues.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
                        <Button 
                            variant="contained" 
                            sx={{ bgcolor: 'white', color: '#1e3c72', fontWeight: 'bold', '&:hover': { bgcolor: '#f8fafc', transform: 'translateY(-2px)' }, transition: 'all 0.2s', borderRadius: 2, px: 3, py: 1.5, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
                            startIcon={<AddIcon />}
                            onClick={() => setOpenAddDialog(true)}
                        >
                            Open New Ticket
                        </Button>
                    </Box>
                </Box>
            </Grow>

            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-line' }}>{error}</Alert>}

            {/* Filters Bar */}
            <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                        <ToggleButtonGroup
                            value={statusFilter}
                            exclusive
                            onChange={(e, newStatus) => { if (newStatus) setStatusFilter(newStatus); }}
                            size="small"
                            sx={{ 
                                '& .MuiToggleButton-root': { 
                                    px: { xs: 2, sm: 3 }, 
                                    py: 1,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: '#64748b',
                                    borderColor: 'divider',
                                    '&.Mui-selected': {
                                        color: '#1e3c72',
                                        bgcolor: 'rgba(30, 60, 114, 0.08)',
                                        '&:hover': { bgcolor: 'rgba(30, 60, 114, 0.12)' }
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="all">All Tickets</ToggleButton>
                            <ToggleButton value="active">Active</ToggleButton>
                            <ToggleButton value="closed">Closed</ToggleButton>
                        </ToggleButtonGroup>

                        <TextField
                            size="small"
                            placeholder="Search my tickets..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                            }}
                            sx={{ 
                                minWidth: { xs: '100%', sm: 300 },
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#f8fafc',
                                    borderRadius: 2,
                                }
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>

            <Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                        <Table sx={{ minWidth: 650 }} aria-label="tickets table">
                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Assigned To</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTickets.map((t) => (
                                    <TableRow
                                        key={t.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'grey.50' } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                {t.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">{t.title}</Typography>
                                            {t.requested_by && <Typography variant="caption" color="textSecondary">Req: {t.requested_by}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={t.description || ''} arrow placement="top">
                                                <Typography variant="body2" color="textSecondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 200 }}>
                                                    {t.description || '-'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{t.type}</TableCell>
                                        <TableCell>{t.country_name || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t.status}
                                                size="small"
                                                color={getStatusColor(t.status)}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(t.priority) }} />
                                                {t.priority || 'Medium'}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {t.assigned_to && t.assigned_to.length > 0 ? (
                                                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                                                    {t.assigned_to.map((u, i) => (
                                                        <Avatar key={u.id || i} alt={u.full_name} src="/static/images/avatar/1.jpg" title={u.full_name}>
                                                            {u.full_name ? u.full_name.charAt(0).toUpperCase() : '?'}
                                                        </Avatar>
                                                    ))}
                                                </AvatarGroup>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">{t.created_at}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{t.due_date || 'N/A'}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredTickets.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body2" color="textSecondary">No tickets found matching your criteria.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            <Dialog 
                open={openAddDialog} 
                onClose={() => !creating && setOpenAddDialog(false)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
            >
                <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Create New Ticket
                    <IconButton size="small" onClick={() => !creating && setOpenAddDialog(false)} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Box component="form" onSubmit={handleCreateTicket} sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Basic Information</Typography>
                            
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Describe your issue or request below. Our AI will automatically categorize and route it for you.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Subject / Title"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    disabled={creating}
                                />
                                
                                <TextField
                                    label="Detailed Description"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    multiline
                                    rows={5}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    disabled={creating}
                                />
                            </Box>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => setOpenAddDialog(false)} disabled={creating} sx={{ borderRadius: 2, fontWeight: '600', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreateTicket} 
                        variant="contained" 
                        color="primary"
                        disabled={creating || !title.trim() || !body.trim()}
                        startIcon={creating ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1.5, boxShadow: '0 4px 14px rgba(30, 60, 114, 0.2)' }}
                    >
                        {creating ? 'Submitting...' : 'Create Ticket'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default UserTickets;
